import { ChevronRight, ChevronDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const last30DaysData = [
  { name: "Botox", value: 28, fullName: "Botox - Upper Face" },
  { name: "Fillers", value: 22, fullName: "Dermal Fillers" },
  { name: "Hydrafacial", value: 18, fullName: "Hydrafacial Treatment" },
  { name: "Microneedling", value: 15, fullName: "Microneedling" },
  { name: "Chemical Peel", value: 12, fullName: "Chemical Peel" },
  { name: "PRP", value: 8, fullName: "PRP Facial" },
  { name: "Laser", value: 6, fullName: "Laser Hair Removal" },
  { name: "LED", value: 5, fullName: "LED Light Therapy" },
];

const last7DaysData = [
  { name: "Botox", value: 8, fullName: "Botox - Upper Face" },
  { name: "Fillers", value: 6, fullName: "Dermal Fillers" },
  { name: "Hydrafacial", value: 5, fullName: "Hydrafacial Treatment" },
  { name: "Microneedling", value: 4, fullName: "Microneedling" },
  { name: "Chemical Peel", value: 3, fullName: "Chemical Peel" },
  { name: "PRP", value: 2, fullName: "PRP Facial" },
  { name: "Laser", value: 2, fullName: "Laser Hair Removal" },
  { name: "LED", value: 1, fullName: "LED Light Therapy" },
];

const last90DaysData = [
  { name: "Botox", value: 85, fullName: "Botox - Upper Face" },
  { name: "Fillers", value: 68, fullName: "Dermal Fillers" },
  { name: "Hydrafacial", value: 52, fullName: "Hydrafacial Treatment" },
  { name: "Microneedling", value: 42, fullName: "Microneedling" },
  { name: "Chemical Peel", value: 35, fullName: "Chemical Peel" },
  { name: "PRP", value: 24, fullName: "PRP Facial" },
  { name: "Laser", value: 18, fullName: "Laser Hair Removal" },
  { name: "LED", value: 15, fullName: "LED Light Therapy" },
];

const colors = [
  "hsl(262, 83%, 58%)",
  "hsl(262, 75%, 62%)",
  "hsl(262, 67%, 66%)",
  "hsl(262, 59%, 70%)",
  "hsl(262, 51%, 74%)",
  "hsl(262, 43%, 78%)",
  "hsl(262, 35%, 82%)",
  "hsl(262, 27%, 86%)",
];

const periodOptions = [
  { label: "Last 7 days", value: "7days", data: last7DaysData },
  { label: "Last 30 days", value: "30days", data: last30DaysData },
  { label: "Last 90 days", value: "90days", data: last90DaysData },
];

const TreatmentStats = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[1]);
  const [showDropdown, setShowDropdown] = useState(false);

  const data = selectedPeriod.data;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const handleViewReport = () => {
    navigate("/aesthetics/treatments");
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-100">
          <p className="font-medium text-gray-900">{payload[0].payload.fullName}</p>
          <p className="text-[#704AAF] font-semibold">{payload[0].value} treatments</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-2xl p-6 shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Treatment Stats</h2>
        <div className="relative">
          <button 
            onClick={() => setShowDropdown(!showDropdown)}
            className="text-[#704AAF] font-medium flex items-center gap-1 hover:text-[#5a3a8f] transition-colors"
          >
            {selectedPeriod.label}
            <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showDropdown && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 min-w-[140px]">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedPeriod(option);
                    setShowDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                    selectedPeriod.value === option.value ? 'text-[#704AAF] font-medium' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-end gap-6">
        <div className="flex flex-col self-start">
          <span className="text-muted-foreground text-sm">Total</span>
          <span className="text-4xl font-bold text-foreground">{total}</span>
          <span className="text-xs text-green-600 mt-1">+12% vs previous</span>
        </div>
        
        <div className="flex-1 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barCategoryGap="15%">
              {/* <XAxis 
                dataKey="name" 
                tick={{ fontSize: 10, fill: "hsl(240, 5%, 46%)" }}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={40}
              /> */}
              <YAxis 
                tick={{ fontSize: 11, fill: "hsl(240, 5%, 46%)" }}
                axisLine={false}
                tickLine={false}
                width={30}
                orientation="right"
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="mt-4 text-right">
        <button 
          onClick={handleViewReport}
          className="text-[#704AAF] hover:underline flex items-center gap-1 ms-auto"
        >
          View all treatments <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TreatmentStats;
